import {
	type ColumnDef,
	DataTable,
	FilterBuilder,
	type FilterValue,
	SearchInput,
} from "@monbits/ui";
import { Activity, User } from "lucide-react";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { AuditLog } from "src/types";
import {
	formatAuditDateTime,
	getCategoryConfig,
	getResourceConfig,
} from "@/utils/audit-log";
import { AUDIT_FILTER_FIELDS } from "./audit-logs-layout";

interface AuditLogsListProps {
	filters: FilterValue[];
	setFilters: (filters: FilterValue[]) => void;
	logs: AuditLog[];
	totalCount: number;
	isLoading: boolean;
	page: number;
	limit: number;
	onPageChange: (page: number) => void;
	onPageSizeChange: (pageSize: number) => void;
}

export function AuditLogsList({
	filters,
	setFilters,
	logs,
	totalCount,
	isLoading,
	page,
	limit,
	onPageChange,
	onPageSizeChange,
}: AuditLogsListProps) {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const search = searchParams.get("search") || "";

	const hasActiveFilters = filters.length > 0 || search.length > 0;

	const handleRowSelect = React.useCallback(
		(log: AuditLog) => {
			navigate(`/audit-logs/${log.id}`);
		},
		[navigate],
	);

	const columns: ColumnDef<AuditLog>[] = React.useMemo(
		() => [
			{
				id: "category",
				header: "Category",
				cell: (log) => {
					const config = getCategoryConfig(log.actionCategory);
					const Icon = config.icon;
					return (
						<div className="flex items-center gap-2">
							<div
								className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0"
								style={{
									backgroundColor: `${config.color}15`,
									color: config.color,
								}}
							>
								<Icon className="h-4 w-4" />
							</div>
							<span className="text-sm font-medium">{config.label}</span>
						</div>
					);
				},
			},
			{
				id: "action",
				header: "Action",
				cell: (log) => (
					<div className="space-y-0.5">
						<div
							className="font-medium text-sm truncate max-w-[300px]"
							title={log.description}
						>
							{log.description}
						</div>
						{log.metadata &&
							typeof log.metadata.error === "string" &&
							log.metadata.error && (
								<div
									className="text-xs text-red-600 truncate"
									title={log.metadata.error}
								>
									Error: {log.metadata.error}
								</div>
							)}
					</div>
				),
			},
			{
				id: "actor",
				header: "Actor",
				cell: (log) => (
					<div className="flex items-center gap-1.5 text-sm">
						<User className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
						<span className="truncate">
							{log.actor.email ||
								`${log.actor.type} ${log.actor.id ? `#${log.actor.id}` : ""}`}
						</span>
					</div>
				),
			},
			{
				id: "resource",
				header: "Resource",
				cell: (log) => {
					const config = getResourceConfig(log.resource.type);
					const Icon = config.icon;
					const resourceIdentifier = log.resource.identifier || log.resource.id;
					let displayIdentifier = resourceIdentifier;

					if (resourceIdentifier) {
						const str = resourceIdentifier.toString();
						if ((str.startsWith("0x") && str.length > 20) || str.length > 30) {
							displayIdentifier = `${str.slice(0, 6)}...${str.slice(-4)}`;
						}
					}

					return (
						<div className="flex items-center gap-2">
							<div
								className="h-7 w-7 rounded flex items-center justify-center flex-shrink-0"
								style={{
									backgroundColor: `${config.color}20`,
									color: config.color,
								}}
							>
								<Icon className="h-4 w-4" />
							</div>
							<div className="space-y-0">
								<div className="text-sm font-medium">{config.label}</div>
								{displayIdentifier && (
									<div
										className="text-xs text-muted-foreground font-mono truncate"
										title={resourceIdentifier?.toString()}
									>
										{displayIdentifier}
									</div>
								)}
							</div>
						</div>
					);
				},
			},
			{
				id: "time",
				header: "Time",
				cell: (log) => (
					<div className="text-sm text-muted-foreground whitespace-nowrap">
						{formatAuditDateTime(log.createdAt)}
					</div>
				),
			},
		],
		[],
	);

	return (
		<div className="p-4 sm:p-6 space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold">Audit Logs</h1>
					<p className="text-sm text-muted-foreground mt-1">
						{totalCount} total log{totalCount !== 1 ? "s" : ""}
					</p>
				</div>
			</div>

			<div className="flex gap-4 flex-wrap items-center">
				<SearchInput
					placeholder="Search by actor email..."
					paramName="search"
					className="w-[300px]"
				/>
				<FilterBuilder
					fields={AUDIT_FILTER_FIELDS}
					filters={filters}
					onFiltersChange={setFilters}
				/>
			</div>

			<DataTable
				columns={columns}
				data={logs}
				isLoading={isLoading}
				onRowClick={handleRowSelect}
				getRowId={(row) => String(row.id)}
				emptyMessage={hasActiveFilters ? "No logs found" : "No audit logs yet"}
				emptyDescription={
					hasActiveFilters
						? "Try adjusting your filter criteria"
						: "Audit logs will appear here as actions are performed"
				}
				emptyIcon={<Activity className="h-6 w-6" />}
				pagination={
					totalCount > 0
						? {
								page,
								pageSize: limit,
								totalCount,
								onPageChange,
								onPageSizeChange,
							}
						: undefined
				}
			/>
		</div>
	);
}
